import React from "react";
import { HiPlus, HiSparkles } from "react-icons/hi";
import { BulkActionBar } from "./BulkActionBar";
import { EmptyScenarios } from "./EmptyScenarios";
import { InlineProgress } from "./InlineProgress";
import { ScenarioEditor } from "./ScenarioEditor";
import { ScenarioList } from "./ScenarioList";
import { UseAgentTestLab } from "./useAgentTestLab";

interface ScenarioWorkbenchProps {
  lab: UseAgentTestLab;
}

export const ScenarioWorkbench: React.FC<ScenarioWorkbenchProps> = ({ lab }) => {
  if (lab.scenarios.length === 0 && lab.scenarioFilter === "all") {
    return (
      <section className="bg-[var(--card)] border border-[var(--border)] rounded-lg overflow-hidden">
        <EmptyScenarios lab={lab} />
      </section>
    );
  }

  const chips: {
    id: typeof lab.scenarioFilter;
    label: string;
    count: number;
    tone?: "default" | "danger" | "warning";
  }[] = [
    { id: "all", label: "All", count: lab.scenarios.length },
    { id: "failed", label: "Failed", count: lab.failedScenarios.length, tone: "danger" },
    {
      id: "needs_review",
      label: "Needs review",
      count: lab.needsReviewScenarios.length,
      tone: "warning",
    },
    { id: "passed", label: "Passed", count: lab.passedScenarios.length },
    {
      id: "edge",
      label: "Edge",
      count: lab.scenarios.filter((s) => s.persona_type === "edge").length,
    },
  ];

  return (
    <section className="bg-[var(--card)] border border-[var(--border)] rounded-lg overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-[var(--border)] flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          {chips.map((chip) => {
            const active = lab.scenarioFilter === chip.id;
            const tone = chip.tone ?? "default";
            const activeStyle =
              tone === "danger"
                ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30"
                : tone === "warning"
                  ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30"
                  : "bg-[var(--accent)]/10 text-[var(--accent)] border-[var(--accent)]/30";
            return (
              <button
                key={chip.id}
                type="button"
                onClick={() => lab.setScenarioFilter(chip.id)}
                disabled={chip.count === 0 && chip.id !== "all"}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors disabled:opacity-40 inline-flex items-center gap-1.5 ${
                  active
                    ? activeStyle
                    : "bg-transparent text-[var(--foreground-muted)] border-[var(--border)] hover:text-[var(--foreground)] hover:bg-[var(--muted)]"
                }`}
              >
                {chip.label}
                <span className="tabular-nums">{chip.count}</span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={lab.handleAddScenario}
            className="px-2.5 py-1.5 text-xs rounded-md hover:bg-[var(--muted)] text-[var(--foreground-muted)] inline-flex items-center gap-1.5"
          >
            <HiPlus className="w-4 h-4" />
            Add
          </button>
          <button
            type="button"
            onClick={lab.handleGenerateScenarios}
            disabled={lab.isGenerating}
            className="px-3 py-1.5 text-xs font-medium bg-[var(--accent)] text-[var(--accent-foreground)] rounded-md hover:bg-[var(--accent-hover)] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <HiSparkles className="w-4 h-4" />
            {lab.isGenerating ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      {(lab.isGenerating || lab.isRunning) && (
        <div className="px-4 pt-3">
          {lab.isGenerating && (
            <InlineProgress
              label="Generating scenarios"
              active={lab.isGenerating}
              hint={`${lab.generationScenarioCount} scenarios · ${lab.maxParallelScenarios} parallel jobs. Slow slots time out server-side; cancelling discards this browser result.`}
              onCancel={lab.cancelGeneration}
            />
          )}
          {lab.isRunning && !lab.isGenerating && (
            <InlineProgress
              label={`Running ${lab.selectedIds.length} scenario${
                lab.selectedIds.length === 1 ? "" : "s"
              }`}
              active={lab.isRunning}
              hint={`${lab.selectedIds.length} scenarios · ${lab.maxParallelScenarios} parallel jobs. Turns stay sequential inside each chat; slow provider steps time out server-side.`}
              onCancel={lab.cancelRun}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] min-h-[640px] flex-1">
        <ScenarioList lab={lab} />
        <div className="flex flex-col min-h-0">
          <ScenarioEditor lab={lab} />
        </div>
      </div>

      <BulkActionBar lab={lab} />
    </section>
  );
};
